import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User as FirebaseUser, 
  signInWithEmailAndPassword,
  signOut, 
  onAuthStateChanged,
  getIdTokenResult,
  sendPasswordResetEmail,
  sendEmailVerification,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { apiClient } from '../services/api';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userClaims: {
    role?: 'admin' | 'user' | 'viewer';
  } | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  /** Send (or resend) email verification link to current user. Throws if no user. */
  sendEmailVerification: () => Promise<void>;
  /** Reload current user from Firebase (e.g. after verifying email). */
  reloadUser: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userClaims, setUserClaims] = useState<{
    role?: 'admin' | 'user' | 'viewer';
  } | null>(null);
  const [loading, setLoading] = useState(true);

  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
      await setPersistence(auth, persistence);
      await signInWithEmailAndPassword(auth, email, password);
      await apiClient.getCurrentUser();
    } catch (error: any) {
      // Do not sign out when email is not verified - user can go to verify-email page
      const isEmailNotVerified = error?.response?.data?.code === 'EMAIL_NOT_VERIFIED';
      if (!isEmailNotVerified) {
        await signOut(auth);
      }
      throw error;
    }
  };

  const register = async (name: string, email: string, password: string) => {
    try {
      await apiClient.registerUser({ name, email, password });
      await signInWithEmailAndPassword(auth, email, password);
      // Send verification email so user can verify before next login
      const user = auth.currentUser;
      if (user) {
        await sendEmailVerification(user);
      }
    } catch (error: any) {
      await signOut(auth);
      throw error;
    }
  };

  const logout = async () => {
    await signOut(auth);
  };

  const forgotPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const sendEmailVerificationLink = async () => {
    const user = auth.currentUser;
    if (!user) throw new Error('You must be signed in to resend the verification email.');
    await sendEmailVerification(user);
  };

  const reloadUser = async () => {
    const user = auth.currentUser;
    if (!user) return;
    await user.reload();
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          const tokenResult = await getIdTokenResult(user);
          setUserClaims({
            role: tokenResult.claims.role as 'admin' | 'user' | 'viewer',
          });
        } catch (error) {
          console.error('Error getting user claims:', error);
          setUserClaims(null);
        }
      } else {
        setUserClaims(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    userClaims,
    login,
    register,
    logout,
    forgotPassword,
    sendEmailVerification: sendEmailVerificationLink,
    reloadUser,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
